import type {
  ActivityRejectionReason,
  ActivityRiskFlag,
  ActivityType,
  CoveredLegs,
  DSAttendanceStatus,
  EffectiveRegistrationStatus,
  EffectiveSourceKind
} from '@/constants/ds-attendance.constants'
import { buildIsoAtOffset, formatHourMinuteInOffset, normalizeUtcOffset } from '@/utils/date'

/* ------------------------------------------------------------------------- *
 * Forma que a estação de auditoria do Registro de Atividade CONSOME.
 * Espelha `ActivityRecordAuditViewDto` (spec F2 §3.4) com TUDO opcional: a tela
 * não pode quebrar por um campo a menos.
 *
 * O backend NÃO serve esta forma numa rota só (achado R2-C2 da jornada em DEV):
 * `GET containers/:id` devolve o container achatado (`kind`, `entryId`,
 * `doctorStationId`, `timezone`, unidade, escala, emissor); o declarado, os anexos
 * e o efetivo de hoje vivem em `GET containers/:id/activity-record` (BF11 — o
 * recorte de `GET /stations/:dsId/registration` com `activityRecords[]` só deste
 * container, `legs` e `frozen`, sob o guard do container e não o da escala); médico
 * e sinais de risco vêm do item da fila (`by-company?kind=ATIVIDADE&doctorStationId=`);
 * o valor já GRAVADO pelo auditor está na entry (`GET containers/:id/entries`).
 * `buildActivityAuditDetail` é a costura entre as quatro — pura, para ser testada
 * com o JSON real de DEV.
 * ------------------------------------------------------------------------- */

export interface ActivityAuditUser {
  id?: number | null
  name?: string | null
}

export interface ActivityAuditDoctorStation {
  id?: number | null
  doctorName?: string | null
  doctorCrm?: string | null
  date?: string | null
  shiftName?: string | null
  expectedStart?: string | null
  expectedEnd?: string | null
  healthUnitName?: string | null
  scheduleName?: string | null
  /** Fuso da escala/unidade (`-03:00`). Ausente no backend antigo: cai em Brasília. */
  timezone?: string | null
}

export interface ActivityAuditPolicy {
  checkinLateToleranceMinutes?: number | null
  checkoutEarlyToleranceMinutes?: number | null
  sourcePrecedence?: Array<EffectiveSourceKind | string> | null
}

export interface ActivityAuditDeclared {
  activityType?: ActivityType | string | null
  activityTypeLabel?: string | null
  activityDescription?: string | null
  coveredLegs?: CoveredLegs | string | null
  checkin?: string | null
  checkout?: string | null
  submittedBy?: ActivityAuditUser | null
  submittedAt?: string | null
}

export interface ActivityAuditAuditorValues {
  entryId?: number | null
  checkin?: string | null
  checkout?: string | null
  overrideReason?: string | null
}

export interface ActivityAuditAttachment {
  evidenceId: number
  originalFileName?: string | null
  fileSizeKb?: number | null
  /**
   * Não existe em `StationRegistrationAttachmentDto`: a costura o preenche com o
   * `evidenceUploadedAt` do item da fila (instante real, lido no fuso da escala).
   */
  uploadedAt?: string | null
  /**
   * Mime real do anexo, quando o backend o manda (nenhum DTO atual tem este campo —
   * ver `ds-attendance-activity-record.response.dto.ts`). Opcional e futuro-compatível:
   * se aparecer, tem prioridade sobre o `Content-Type` do download na escolha do
   * visualizador (`classifyAttachmentViewer`).
   */
  mimeType?: string | null
}

/**
 * Fonte de uma perna. O backend da F2 manda um OBJETO (`{kind, label, method}`) na
 * prévia e na registration; a forma escalar é a que a W1 supôs e a que as fixtures
 * antigas usam. As duas são aceitas em todo lugar — interpolar o objeto como string
 * foi o `[object Object]` do achado R2-I1.
 */
export type ActivityAuditSource =
  | EffectiveSourceKind
  | string
  | { kind?: EffectiveSourceKind | string | null; label?: string | null; method?: string | null }
  | null

export interface ActivityAuditEffectiveLeg {
  value?: string | null
  source?: ActivityAuditSource
}

export interface ActivityAuditEffectiveView {
  checkin?: ActivityAuditEffectiveLeg | null
  checkout?: ActivityAuditEffectiveLeg | null
  registrationStatus?: EffectiveRegistrationStatus | string | null
}

export interface ActivityAuditDetail {
  id?: number
  status?: DSAttendanceStatus | string | null
  entryId?: number | null
  doctorStation?: ActivityAuditDoctorStation | null
  policy?: ActivityAuditPolicy | null
  declared?: ActivityAuditDeclared | null
  auditor?: ActivityAuditAuditorValues | null
  attachments?: ActivityAuditAttachment[] | null
  effectiveNow?: ActivityAuditEffectiveView | null
  postStation?: { status?: string | null; frozenAt?: string | null } | null
  previousContainerId?: number | null
  activityRiskFlags?: Partial<Record<ActivityRiskFlag, boolean>> | null
  legacyMigrated?: boolean
  afterClosure?: boolean
  /** Veredito já dado — só lido quando o status sai de `PENDING_AUDIT`/`AUDIT_IN_PROGRESS`. */
  rejectionReason?: ActivityRejectionReason | string | null
  rejectionJustification?: string | null
  justification?: string | null
  reviewedBy?: ActivityAuditUser | null
  reviewedAt?: string | null
}

/* ------------------------------------------------------------------------- *
 * As quatro fontes, como o backend as serve HOJE. Tudo opcional pelo mesmo
 * motivo de sempre: o web deploya antes, e nenhuma ausência pode quebrar a tela.
 * ------------------------------------------------------------------------- */

/** `GET /ds-attendance/operations/containers/:id` — o container achatado. */
export interface ActivityContainerDetailSource {
  id?: number
  status?: DSAttendanceStatus | string | null
  kind?: string | null
  entryId?: number | null
  doctorStationId?: number | null
  timezone?: string | null
  referenceDate?: string | null
  healthUnit?: { id?: number; name?: string | null } | null
  schedule?: { id?: number; name?: string | null } | null
  shifts?: Array<{ id?: number; name?: string | null }> | null
  submittedBy?: ActivityAuditUser | null
  submittedAt?: string | null
  reviewedBy?: ActivityAuditUser | null
  reviewedAt?: string | null
  rejectionReason?: string | null
  previousContainerId?: number | null
  requestedLeg?: string | null
}

/** Uma perna da registration / do activity-record (`legs.checkin` / `checkin`). */
export interface ActivityRegistrationLegSource {
  /** Horário efetivo. `at` é o nome do contrato; `value` é o da tipagem antiga do web. */
  at?: string | null
  value?: string | null
  expected?: string | null
  source?: ActivityAuditSource
}

/** Um item de `activityRecords[]` — o mesmo na registration e no activity-record. */
export interface ActivityRegistrationRecordSource {
  containerId?: number | null
  status?: DSAttendanceStatus | string | null
  activityType?: ActivityType | string | null
  activityTypeLabel?: string | null
  activityDescription?: string | null
  coveredLegs?: CoveredLegs | string | null
  declaredCheckin?: string | null
  declaredCheckout?: string | null
  attachments?: ActivityAuditAttachment[] | null
  submittedBy?: ActivityAuditUser | null
  submittedAt?: string | null
  reviewedBy?: ActivityAuditUser | null
  reviewedAt?: string | null
  rejectionReason?: string | null
  previousContainerId?: number | null
  afterClosure?: boolean | null
  legacyMigrated?: boolean | null
}

/**
 * A forma da registration do plantão, no recorte que a auditoria usa. É o que
 * `GET containers/:id/activity-record` serve (`doctorStationId`, `timezone`,
 * `registrationStatus`, `registrationStatusLabel`, `activityRecords`, `legs`, `frozen`)
 * — byte-compatível com `GET /stations/:dsId/registration`, que o Detalhes do Plantão
 * continua lendo. `status`/`frozenAt`/`checkin`/`checkout` são da tipagem antiga do web;
 * tudo é opcional e, no que faltar, a costura cai no item da fila.
 */
export interface ActivityRegistrationSource {
  doctorStationId?: number | null
  timezone?: string | null
  registrationStatus?: EffectiveRegistrationStatus | string | null
  registrationStatusLabel?: string | null
  /** Tipagem antiga do web chama o estado de `status`. */
  status?: EffectiveRegistrationStatus | string | null
  frozenAt?: string | null
  frozen?: { at?: string | null; reason?: string | null } | boolean | null
  legs?: {
    checkin?: ActivityRegistrationLegSource | null
    checkout?: ActivityRegistrationLegSource | null
  } | null
  checkin?: ActivityRegistrationLegSource | null
  checkout?: ActivityRegistrationLegSource | null
  activityRecords?: ActivityRegistrationRecordSource[] | null
}

/** O item de `by-company?kind=ATIVIDADE&doctorStationId=` deste container. */
export interface ActivityListItemSource {
  id?: number
  doctorName?: string | null
  doctorCrm?: string | null
  activityType?: ActivityType | string | null
  activityTypeLabel?: string | null
  activityDescription?: string | null
  coveredLegs?: CoveredLegs | string | null
  declaredCheckin?: string | null
  declaredCheckout?: string | null
  timezone?: string | null
  submittedByName?: string | null
  submittedAt?: string | null
  activityRiskFlags?: Partial<Record<ActivityRiskFlag, boolean>> | null
  previousContainerId?: number | null
  afterClosure?: boolean | null
  legacyMigrated?: boolean | null
  /** Instante real do upload do anexo (`@CreateDateColumn`), único por Registro de Atividade. */
  evidenceUploadedAt?: string | null
  effectiveSummary?: {
    checkin?: string | null
    checkout?: string | null
    checkinSource?: ActivityAuditSource
    checkoutSource?: ActivityAuditSource
    registrationStatus?: EffectiveRegistrationStatus | string | null
    frozenAt?: string | null
  } | null
}

/**
 * A entry única do Registro de Atividade (`GET containers/:id/entries`).
 *
 * `checkinReal`/`checkoutReal`/`declaredCheckin`/`declaredCheckout` são colunas
 * `timestamp` SEM fuso (parede do plantão, regra BF7). O backend da BF12 as serializa
 * como ISO COM o offset do plantão (`2026-09-12T08:10:00.000-04:00`); o anterior
 * devolvia a entidade crua, lida pelo `pg` no `TZ` do processo e serializada com `Z`
 * (`11:10Z` para a parede 08:10 — só igual em Brasília). `auditorOf` reconhece os dois
 * formatos: ver `auditorLegOf`.
 */
export interface ActivityEntrySource {
  id?: number
  checkinReal?: string | null
  checkoutReal?: string | null
  declaredCheckin?: string | null
  declaredCheckout?: string | null
  auditorOverrideReason?: string | null
}

export interface ActivityAuditDetailSources {
  detail: ActivityContainerDetailSource
  /** `GET containers/:id/activity-record` (recorte da registration deste container). */
  activityRecord?: ActivityRegistrationSource | null
  listItem?: ActivityListItemSource | null
  entry?: ActivityEntrySource | null
}

/* ------------------------------------------------------------------------- */

/** `kind` de uma fonte, seja ela escalar ou o objeto `{kind, label}` do backend. */
export function sourceKind(source: ActivityAuditSource | undefined): string | null {
  if (!source) return null
  if (typeof source === 'object') return source.kind ? String(source.kind) : null
  return String(source)
}

/** Rótulo pronto do servidor, quando a fonte veio como objeto com `label`. */
export function sourceServerLabel(source: ActivityAuditSource | undefined): string | null {
  if (!source || typeof source !== 'object') return null
  return source.label || null
}

/**
 * A resposta JÁ está na forma consumida pela tela? É o caso das fixtures e de um
 * backend futuro que sirva `ActivityRecordAuditViewDto` de uma vez. Sem `declared`
 * nem `doctorStation` é o container achatado, que precisa ser costurado.
 */
export function isActivityAuditDetailShape(raw: unknown): raw is ActivityAuditDetail {
  if (!raw || typeof raw !== 'object') return false
  return 'declared' in raw || 'doctorStation' in raw
}

function registrationLeg(
  registration: ActivityRegistrationSource | null | undefined,
  leg: 'checkin' | 'checkout'
): ActivityRegistrationLegSource | null {
  return registration?.legs?.[leg] ?? registration?.[leg] ?? null
}

function effectiveLeg(
  sources: ActivityAuditDetailSources,
  leg: 'checkin' | 'checkout'
): ActivityAuditEffectiveLeg | null {
  const fromRegistration = registrationLeg(sources.activityRecord, leg)
  if (fromRegistration) {
    const value = fromRegistration.at ?? fromRegistration.value ?? null
    return { value, source: value ? sourceKind(fromRegistration.source) : null }
  }
  const summary = sources.listItem?.effectiveSummary
  if (!summary) return null
  const value = summary[leg] ?? null
  const source = leg === 'checkin' ? summary.checkinSource : summary.checkoutSource
  return { value, source: value ? sourceKind(source) : null }
}

function frozenAtOf(sources: ActivityAuditDetailSources): string | null {
  const activityRecord = sources.activityRecord
  if (activityRecord?.frozenAt) return activityRecord.frozenAt
  const frozen = activityRecord?.frozen
  if (frozen && typeof frozen === 'object' && frozen.at) return frozen.at
  return sources.listItem?.effectiveSummary?.frozenAt ?? null
}

function shiftNameOf(detail: ActivityContainerDetailSource): string | null {
  const names = (detail.shifts ?? []).map((shift) => shift?.name).filter(Boolean)
  return names.length ? names.join(', ') : null
}

function recordOf(sources: ActivityAuditDetailSources): ActivityRegistrationRecordSource | null {
  const records = sources.activityRecord?.activityRecords ?? []
  const id = sources.detail.id
  return records.find((record) => Number(record.containerId) === Number(id)) ?? null
}

function first<T>(...values: Array<T | null | undefined>): T | null {
  for (const value of values) if (value !== null && value !== undefined) return value
  return null
}

function doctorStationOf(sources: ActivityAuditDetailSources): ActivityAuditDoctorStation {
  const { detail, activityRecord, listItem } = sources
  return {
    id: detail.doctorStationId ?? null,
    doctorName: listItem?.doctorName ?? null,
    doctorCrm: listItem?.doctorCrm ?? null,
    date: detail.referenceDate ?? null,
    shiftName: shiftNameOf(detail),
    expectedStart: registrationLeg(activityRecord, 'checkin')?.expected ?? null,
    expectedEnd: registrationLeg(activityRecord, 'checkout')?.expected ?? null,
    healthUnitName: detail.healthUnit?.name ?? null,
    scheduleName: detail.schedule?.name ?? null,
    timezone: first(detail.timezone, activityRecord?.timezone, listItem?.timezone)
  }
}

function declaredOf(
  sources: ActivityAuditDetailSources,
  record: ActivityRegistrationRecordSource | null
): ActivityAuditDeclared {
  const { detail, listItem } = sources
  const submittedBy = first<ActivityAuditUser>(
    record?.submittedBy,
    detail.submittedBy,
    listItem?.submittedByName ? { name: listItem.submittedByName } : null
  )
  return {
    activityType: first(record?.activityType, listItem?.activityType),
    activityTypeLabel: first(record?.activityTypeLabel, listItem?.activityTypeLabel),
    activityDescription: first(record?.activityDescription, listItem?.activityDescription),
    coveredLegs: first(record?.coveredLegs, listItem?.coveredLegs),
    checkin: first(record?.declaredCheckin, listItem?.declaredCheckin),
    checkout: first(record?.declaredCheckout, listItem?.declaredCheckout),
    submittedBy,
    submittedAt: first(record?.submittedAt, detail.submittedAt, listItem?.submittedAt)
  }
}

/** `YYYY-MM-DDTHH:mm[:ss[.sss]]` sem NENHUMA marca de fuso (`Z` ou offset) — parede crua. */
const ISO_NAIVE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?$/

/** Termina em `Z` — o formato que o backend anterior à BF12 gravava (sempre UTC). */
const ISO_UTC_Z = /Z$/

function sameInstant(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false
  const left = new Date(a).getTime()
  const right = new Date(b).getTime()
  return !Number.isNaN(left) && left === right
}

/**
 * Desloca `base` (ISO já carimbado com offset explícito) pelo MESMO delta que separa os
 * instantes `from` → `to`, reconstruindo a saída no offset do plantão (nunca o do
 * processo/navegador). Usado só pelo achado Opus W-N2 (ver `auditorLegOf`): quando a
 * sobrescrita do auditor chega em `Z` (backend anterior à BF12), `real` não pode ser lido
 * como instante puro fora de `-03:00` — mas o DESVIO que ele representa em relação ao
 * declarado da entry é um dado confiável, e aplicá-lo ao declarado carimbado (que já tem o
 * offset certo) devolve a hora de parede que o auditor realmente gravou.
 *
 * `null` se qualquer um dos três ISOs for inválido, ou se `base` não tiver offset explícito
 * (achado Opus M2 — uma parede crua seria lida no fuso do processo, não no do plantão) —
 * quem chama mantém o valor cru nesse caso, nunca lança.
 *
 * @param base ISO já carimbado com o offset DO PLANTÃO (ex.: `registrationDeclared`,
 * `2026-09-12T08:10:00-04:00`) — nunca uma string em `Z`. É esse offset que a saída herda.
 */
function shiftByDelta(
  base: string,
  from: string,
  to: string,
  offset: string | null | undefined
): string | null {
  // Achado Opus M2: `base` PRECISA chegar com offset explícito (ver JSDoc acima). Uma
  // parede crua (`ISO_NAIVE`) seria lida por `new Date` no fuso do PROCESSO/navegador, não
  // no do plantão — melhor devolver `null` (quem chama mantém `real` cru) do que herdar um
  // erro de fuso do ambiente.
  if (ISO_NAIVE.test(base)) return null

  const fromMs = new Date(from).getTime()
  const toMs = new Date(to).getTime()
  const baseMs = new Date(base).getTime()
  if (Number.isNaN(fromMs) || Number.isNaN(toMs) || Number.isNaN(baseMs)) return null

  const normalizedOffset = normalizeUtcOffset(offset ?? undefined)
  const shiftedInstant = new Date(baseMs + (toMs - fromMs)).toISOString()
  const hhmm = formatHourMinuteInOffset(shiftedInstant, normalizedOffset)
  if (!hhmm) return null
  return buildIsoAtOffset(shiftedInstant, hhmm, normalizedOffset)
}

/**
 * O valor GRAVADO de uma perna, num ISO que `toHHmm(value, scheduleOffset)` lê certo em
 * qualquer fuso (achado Opus IMPORTANT-2). Três formatos chegam da entry:
 *
 *  - **com offset numérico** (BF12, `08:10-04:00`): é parede carimbada — passa intacto e
 *    o parse usa o offset DA STRING, nunca o do navegador;
 *  - **sem marca de fuso** (`2026-09-12T08:10:00`): parede crua — ganha o offset do
 *    plantão, senão o `new Date` do navegador a leria no fuso da máquina do auditor;
 *  - **com `Z`** (backend anterior à BF12): a parede lida no `TZ` do processo — um
 *    instante que só coincide com o plantão em Brasília. Enquanto o auditor não
 *    sobrescreveu, `checkinReal` é o MESMO instante que o `declaredCheckin` da própria
 *    entry (ambos nascem do mesmo `wallClockFromInstant`), e aí o valor certo é o
 *    declarado carimbado da registration: é isso que impede a tela de abrir com o
 *    campo 1 h/2 h diferente do declarado numa escala `-04:00`/`-05:00`, acender
 *    "motivo obrigatório" e travar "Aprovar" por uma alteração que ninguém fez.
 */
function auditorLegOf(
  real: string | null | undefined,
  entryDeclared: string | null | undefined,
  registrationDeclared: string | null | undefined,
  timezone: string | null | undefined
): string | null {
  if (!real) return null
  if (registrationDeclared && sameInstant(real, entryDeclared)) return registrationDeclared
  if (ISO_NAIVE.test(real)) return `${real}${normalizeUtcOffset(timezone ?? undefined)}`
  if (ISO_UTC_Z.test(real) && registrationDeclared && entryDeclared) {
    // Legado `Z` COM sobrescrita real (achado Opus W-N2): `real` é a parede lida no TZ do
    // PROCESSO, não do plantão — fora de -03:00 isso mostra ao auditor uma hora errada.
    // Aplica ao declarado carimbado (que já tem o offset certo) o mesmo delta que separa
    // a sobrescrita do declarado da entry, em vez de devolver `real` cru.
    const shifted = shiftByDelta(registrationDeclared, entryDeclared, real, timezone)
    if (shifted) return shifted
  }
  // Com offset numérico explícito (BF12): já é parede carimbada, passa intacto.
  return real
}

function auditorOf(
  sources: ActivityAuditDetailSources,
  declared: ActivityAuditDeclared,
  timezone: string | null | undefined
): ActivityAuditAuditorValues {
  const { detail, entry } = sources
  return {
    entryId: first(detail.entryId, entry?.id),
    checkin: auditorLegOf(entry?.checkinReal, entry?.declaredCheckin, declared.checkin, timezone),
    checkout: auditorLegOf(
      entry?.checkoutReal,
      entry?.declaredCheckout,
      declared.checkout,
      timezone
    ),
    overrideReason: entry?.auditorOverrideReason ?? null
  }
}

function effectiveNowOf(sources: ActivityAuditDetailSources): ActivityAuditEffectiveView | null {
  const { activityRecord, listItem } = sources
  if (!activityRecord && !listItem?.effectiveSummary) return null
  return {
    checkin: effectiveLeg(sources, 'checkin'),
    checkout: effectiveLeg(sources, 'checkout'),
    // O activity-record (BF11) serve `registrationStatus` (o `Pick<>` do DTO o inclui);
    // `status` é o nome da tipagem antiga e o item da fila é o fallback para o backend
    // anterior à rota.
    registrationStatus: first(
      activityRecord?.registrationStatus,
      activityRecord?.status,
      listItem?.effectiveSummary?.registrationStatus
    )
  }
}

/**
 * Costura o container achatado com o activity-record, o item da fila e a entry na
 * forma que a tela consome. Precedência por campo:
 *  - declarado/anexos: activity-record (é a fonte SQL do registro) → item da fila;
 *  - médico/CRM/sinais de risco: item da fila (o activity-record não os tem);
 *  - unidade/escala/turnos/data: container;
 *  - emissor: activity-record → container → item da fila (`submittedByName`);
 *  - valor do auditor: entry (`checkinReal`/`checkoutReal`/`auditorOverrideReason`),
 *    normalizado para o fuso do plantão (`auditorLegOf`);
 *  - data de envio do anexo: o DTO do anexo não a tem; vem do `evidenceUploadedAt` do
 *    item da fila (um upload por Registro de Atividade) → `submittedAt` do registro.
 * `null`/ausência em qualquer fonte nunca derruba as outras.
 */
export function buildActivityAuditDetail(sources: ActivityAuditDetailSources): ActivityAuditDetail {
  const { detail, listItem } = sources
  const record = recordOf(sources)
  const frozenAt = frozenAtOf(sources)
  const doctorStation = doctorStationOf(sources)
  const declared = declaredOf(sources, record)
  const uploadedAt = first(listItem?.evidenceUploadedAt, record?.submittedAt)

  return {
    id: detail.id,
    status: first(detail.status, record?.status),
    entryId: first(detail.entryId, sources.entry?.id),
    doctorStation,
    policy: null,
    declared,
    auditor: auditorOf(sources, declared, doctorStation.timezone),
    attachments: (record?.attachments ?? []).map((attachment) => ({
      ...attachment,
      uploadedAt: attachment.uploadedAt ?? uploadedAt
    })),
    effectiveNow: effectiveNowOf(sources),
    postStation: { status: frozenAt ? 'APPROVED' : null, frozenAt },
    previousContainerId: first(
      record?.previousContainerId,
      listItem?.previousContainerId,
      detail.previousContainerId
    ),
    activityRiskFlags: listItem?.activityRiskFlags ?? null,
    legacyMigrated: first(record?.legacyMigrated, listItem?.legacyMigrated) ?? false,
    afterClosure: first(record?.afterClosure, listItem?.afterClosure) ?? false,
    rejectionReason: first(detail.rejectionReason, record?.rejectionReason),
    reviewedBy: first(detail.reviewedBy, record?.reviewedBy),
    reviewedAt: first(detail.reviewedAt, record?.reviewedAt)
  }
}
